import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import { Grid } from '@material-ui/core';
import { withStyles, WithStyles } from '@material-ui/styles';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import { getUserDetails, updateUserDetails, getAccountDetails } from '../../../services/users/users.service';
import PersonalData from '../../../components/UserData/PersonalData.component';
import AccountData from '../../../components/UserData/AccountData.component';
import { UserM, AccountTypeEnum } from '../../../models/user.types';
import EditExaminerForm from '../../../components/EditUser/EditUserForm.component';
import { useStyles, styles, styles2 } from './UserDetails.styles';
import { AxiosResponse } from 'axios';
import { useLayoutContext } from '../../../Context/Layout.context';
import { getExamCommitteeNameFromIdString } from '../../SampleAssessmentRepo/Helpers/AccountTypeRendering';
import { SecurityEventTypeEnum } from '../../../services/users/users.types';
import CloseButton from '../../../components/buttons/CloseButtton.component';

export interface DialogTitleProps extends WithStyles<typeof styles> {
  id: string;
  children: React.ReactNode;
  onClose: () => void;
}

const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
  const { children, classes, ...other } = props;
  return (
    <MuiDialogTitle className={classes.root} {...other}>
      {children}
    </MuiDialogTitle>
  );
});

export interface DialogTitleProps2 extends WithStyles<typeof styles2> {
  id: string;
  children: React.ReactNode;
  onClose: () => void;
}

const DialogTitle2 = withStyles(styles2)((props: DialogTitleProps2) => {
  const { children, classes, ...other } = props;
  return (
    <MuiDialogTitle className={classes.root} {...other}>
      {children}
    </MuiDialogTitle>
  );
});

interface UserDetailsProps {
  id: string;
  isUserUpdated: () => void;
  onClose: () => void;
}

export default function UserDetails(props: UserDetailsProps) {
  const [showPersonalData, setShowPersonalData] = useState<boolean>(true);
  const [showDataEdit, setShowDataEdit] = useState<boolean>(false);
  const [user, setUser] = useState<UserM | undefined>(undefined);
  const [accountCreated, setAccountCreated] = useState<string>('');
  const [accountModified, setAccountModified] = useState<string>('');
  const [lastModifierName, setLastModifierName] = useState<string>('');
  const [lastModifierSurname, setLastModifierSurname] = useState<string>('');
  const [lastLogin, setLastLogin] = useState<string>('');
  const [lastForcePasswordChange, setLastForcePasswordChange] = useState<string>('');
  const { openAlert } = useLayoutContext();

  const getAccountAllDetails = () => {
    getAccountDetails(props.id, SecurityEventTypeEnum.UserModifiedEvent).then((result) => {
      if (result) {
        setAccountModified(result.timeStamp);
        setLastModifierSurname(result.submitter.lastName);
        setLastModifierName(result.submitter.firstName);
      }
    });
    getAccountDetails(props.id, SecurityEventTypeEnum.UserCreatedEvent).then((result) => {
      if (result) {
        setAccountCreated(result.timeStamp);
      }
    });
    getAccountDetails(props.id, SecurityEventTypeEnum.LoggedInEvent).then((result) => {
      if (result) {
        setLastLogin(result.timeStamp);
      }
    });
    getAccountDetails(props.id, SecurityEventTypeEnum.UserForcedPasswordChange).then((result) => {
      if (result) {
        setLastForcePasswordChange(result.timeStamp);
      }
    });
  };

  useEffect(() => {
    if (props.id) {
      getUserDetails(props.id).then((result: UserM) => {
        setUser(result);
      });
      getAccountAllDetails();
    }
  }, [props.id]);

  const classes = useStyles();
  const personalDataTextClass = showPersonalData ? classes.activeText : classes.inactiveText;
  const personalDataClass = showPersonalData ? classes.active : classes.inactive;
  const accountDataTextClass = showPersonalData ? classes.inactiveText : classes.activeText;
  const accountDataClass = showPersonalData ? classes.inactive : classes.active;

  const editData = () => {
    setShowPersonalData(false);
    setShowDataEdit(true);
  };

  const goToDataView = () => {
    setShowPersonalData(true);
    setShowDataEdit(false);
  };

  const updateUser = async (
    id: string,
    firstName: string,
    lastName: string,
    phone: string,
    phoneHidden: boolean,
    email: string,
    accountTypeId: AccountTypeEnum,
    examCommiteeId: number,
    pesel: string,
    expirationDate?: string,
  ) => {
    const examCommiteeIdProps = examCommiteeId;
    await updateUserDetails(
      id,
      firstName,
      lastName,
      phone,
      phoneHidden,
      email,
      accountTypeId,
      examCommiteeIdProps,
      pesel,
      expirationDate,
    )
      .then(
        (result: AxiosResponse<number>) => {
          if (result.status === 200 || result.status === 204) {
            openAlert('success', 'Pomyślnie zaktualizowano dane.');
            props.isUserUpdated();
          }
        },
        () => openAlert('save_error'),
      )
      .then(() => {
        getUserDetails(props.id)
          .then(
            (result) => {
              setUser(result);
            },
            () => openAlert('get_error'),
          )
          .then(goToDataView);
        getAccountAllDetails();
      });
  };

  return (
    <Dialog onClose={props.onClose} aria-labelledby="customized-dialog-title" open={true} maxWidth={false}>
      {showPersonalData && (
        <DialogTitle id="customized-dialog-title" onClose={props.onClose}>
          <Grid container>
            <Grid item xs={6}>
              <div className={classes.modalTitle}>
                {user?.firstName} {user?.lastName}
              </div>
            </Grid>
            <Grid item xs={6}>
              <CloseButton onClick={props.onClose} />
            </Grid>

            <Grid item xs={3}>
              <div onClick={() => setShowPersonalData(true)} className={personalDataClass}>
                <div className={personalDataTextClass}>Dane osobowe</div>
              </div>
              <div onClick={() => setShowPersonalData(false)} className={accountDataClass}>
                <div className={accountDataTextClass}>Informacja o koncie</div>
              </div>
            </Grid>
            <Grid item xs={9}>
              <PersonalData
                firstName={user?.firstName}
                lastName={user?.lastName}
                email={user?.email}
                pesel={user?.pesel}
                accountTypeName={user?.accountTypeName}
                committee={getExamCommitteeNameFromIdString(user?.examCommitteeId)}
                phone={user?.phone}
                editData={editData}
              />
            </Grid>
          </Grid>
        </DialogTitle>
      )}
      {!showPersonalData && !showDataEdit && (
        <DialogTitle id="customized-dialog-title" onClose={props.onClose}>
          <Grid container>
            <Grid item xs={6}>
              <div className={classes.modalTitle}>
                {user?.firstName} {user?.lastName}
              </div>
            </Grid>
            <Grid item xs={6}>
              <CloseButton onClick={props.onClose} />
            </Grid>

            <Grid item xs={3}>
              <div onClick={() => setShowPersonalData(true)} className={personalDataClass}>
                <div className={personalDataTextClass}>Dane osobowe</div>
              </div>
              <div onClick={() => setShowPersonalData(false)} className={accountDataClass}>
                <div className={accountDataTextClass}>Informacja o koncie</div>
              </div>
            </Grid>
            <Grid item xs={9}>
              <AccountData
                expirationDate={user?.expirationDate}
                emailConfirmed={user?.emailConfirmed}
                isForcedPasswordChange={user?.forcedPasswordChange}
                isVisible={!showPersonalData}
                userId={props.id}
                accountCreated={accountCreated}
                accountModified={accountModified}
                lastModifierName={lastModifierName}
                lastModifierSurname={lastModifierSurname}
                lastLogin={lastLogin}
                lastForcePasswordChange={lastForcePasswordChange}
              />
            </Grid>
          </Grid>
        </DialogTitle>
      )}
      {!showPersonalData && showDataEdit && user && (
        <DialogTitle2 id="customized-dialog-title" onClose={props.onClose}>
          <Grid container>
            <EditExaminerForm
              userArg={user}
              goToDataView={goToDataView}
              closeModal={props.onClose}
              updateUser={updateUser}
            />
          </Grid>
        </DialogTitle2>
      )}
    </Dialog>
  );
}
